import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { 
  MdSettings, 
  MdPersonAdd, 
  MdHistory, 
  MdEdit, 
  MdCheckCircle, 
  MdError,
  MdSearch,
  MdFilterList,
  MdSecurity,
  MdCameraAlt,
  MdBusiness
} from "react-icons/md";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import { AuthState } from "../../context/AuthProvider";
import { useTheme } from "../../context/ThemeContext";
import { API } from "../../utils";

// Get initials from name
const getInitials = (name = "") => {
  const cleanName = name.trim();
  if (!cleanName) return "?";
  const parts = cleanName.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  // If only one name, take first 2 letters
  return cleanName.substring(0, 2).toUpperCase();
};

const avatarColor = (name = "") => {
  const colors = [
    "bg-blue-500", "bg-purple-500", "bg-green-500",
    "bg-rose-500", "bg-amber-500", "bg-teal-500",
    "bg-indigo-500", "bg-pink-500"
  ];
  return colors[(name.charCodeAt(0) || 0) % colors.length];
};

const Settings = () => {
  const { auth, setAuth } = AuthState();
  const isAdmin = auth?.role === "Admin";
  const { language, setLanguage, t } = useLanguage();
  const isStaff = auth?.role === "Staff";
  const { isDarkMode, toggleTheme, primaryColor, setPrimaryColor } = useTheme();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null); // For mobile detail popover
  
  // User Form State
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "Staff",
    profilePic: ""
  });
  
  const [systemLogo, setSystemLogo] = useState(localStorage.getItem("systemLogo") || "");
  const [systemName, setSystemName] = useState(localStorage.getItem("systemName") || "Kaviraj Agencies");
  const highlightUserId = new URLSearchParams(location.search).get("highlight");
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await API.get("/api/admin/users");
      const result = response.data;
      if (result.success) {
        setUsers(result.data);
      } else {
        toast.error(result.error || t("settings.failedToFetchUsers"));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || t("settings.couldNotReachServer");
      toast.error(t("common.error") + ": " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await API.get("/api/admin/audit-logs");
      const result = response.data;
      if (result.success) setLogs(result.data);
    } catch (err) {
      toast.error(t("settings.failedToFetchLogs"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "users") fetchUsers();
    if (activeTab === "logs" && isAdmin){ fetchLogs()};
  }, [activeTab, isAdmin]);

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    const url = editingUser ? `/api/admin/users/${editingUser._id}` : "/api/admin/users";

    try {
      const response = editingUser 
        ? await API.put(url, formData)
        : await API.post(url, formData);
      const result = response.data;
      if (result.success) {
        toast.success(editingUser ? "User updated!" : "User created!");
        
        // If updating the current user, sync local state
        if (editingUser && editingUser._id === auth._id) {
          const updatedAuth = { ...auth, ...formData };
          localStorage.setItem("auth", JSON.stringify(updatedAuth));
          setAuth(updatedAuth);
        }

        setShowUserModal(false);
        setEditingUser(null);
        setFormData({ name: "", email: "", password: "", role: "Staff" });
        fetchUsers();
      } else {
        toast.error(result.error || t("settings.actionFailed"));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || t("common.error");
      toast.error(errorMsg);
    }
  };

  const handleFileChange = (e, target) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file (JPG, PNG, etc.)");
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) { 
        toast.error("File size too large. Please upload an image under 10MB.");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        if (target === 'user') {
          setFormData(prev => ({ ...prev, profilePic: reader.result }));
        } else if (target === 'system') {
          setSystemLogo(reader.result);
          localStorage.setItem("systemLogo", reader.result);
          toast.success("System logo updated!");
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSystemNameChange = (newName) => {
    setSystemName(newName);
    localStorage.setItem("systemName", newName);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      profilePic: user.profilePic || ""
    });
    setShowUserModal(true);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 text-gray-900 dark:text-white transition-all duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-2.5 rounded-lg">
            <MdSettings className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex items-center space-x-3">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 tracking-tight">{t("settings.title")}</h1>
            <p className="text-xs sm:text-sm text-gray-500">{t("settings.manageUsers")}</p>
          </div>
        </div>
        {activeTab === "users" && isAdmin && (
          <button
            onClick={() => { setEditingUser(null); setFormData({name: "", email: "", password: "", role: "Staff"}); setShowUserModal(true); }}
            className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-sm transition-all shadow-md active:scale-95 w-full sm:w-auto"
          >
            <MdPersonAdd className="w-5 h-5" />
            <span>{t("settings.addNewUser")}</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-200/50 p-1 rounded-xl w-full sm:w-fit overflow-x-auto">
        <button 
          onClick={() => setActiveTab("users")}
          className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'users' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <MdSecurity className="w-4 h-4" />
          <span>{t("settings.userManagement")}</span>
        </button>
        {isAdmin && (
            <button 
              onClick={() => setActiveTab("logs")}
              className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'logs' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <MdHistory className="w-4 h-4" />
              <span>{t("settings.auditLogs")}</span>
            </button>
          )}
        <button 
          onClick={() => setActiveTab("general")}
          className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'general' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <MdSettings className="w-4 h-4" />
          <span>{t("settings.generalSettings")}</span>
        </button>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden min-h-[500px]">
        {activeTab === "users" ? (
          <div>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-400 uppercase text-[11px] font-bold tracking-widest border-b border-gray-100">
                    <th className="px-6 py-4">{t("settings.name")}</th>
                    <th className="px-6 py-4">{t("settings.email")}</th>
                    <th className="px-6 py-4">{t("settings.role")}</th>
                    <th className="px-6 py-4">{t("settings.status")}</th>
                    <th className="px-6 py-4">{t("settings.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-10 text-center">
                        <div className="flex flex-col items-center justify-center space-y-2 text-blue-600">
                          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                          <span className="text-xs font-bold uppercase tracking-widest">{t("common.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-10 text-center text-gray-400 italic">
                        {t("settings.noUsersFound")}
                      </td>
                    </tr>
                  ) : (
                    users.filter(user => isAdmin || user._id === auth?._id).map((user) => (
                        <tr
  key={user._id}
  className={`
    transition-all
    duration-500
    group
    ${
      highlightUserId === user._id
        ? "bg-blue-50 ring-2 ring-blue-400 shadow-[0_0_30px_rgba(59,130,246,0.25)] animate-pulse"
        : "hover:bg-blue-50/30"
    }
  `}
>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${avatarColor(user.name)}`}>
                                {getInitials(user.name)}
                              </div>
                              <span className="font-bold text-gray-700">{user.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-500">{user.email}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${user.role?.toLowerCase() === 'admin' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>{user.role}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-1 text-green-600 text-[11px] font-bold">
                              <MdCheckCircle className="w-4 h-4" />
                              <span>{t("settings.active")}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {isAdmin && (
                              <button 
                                onClick={() => openEditModal(user)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-all"
                              >
                                <MdEdit className="w-5 h-5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden grid grid-cols-1 gap-4 p-4 bg-gray-50/50">
              {loading ? (
                <div className="text-center py-10 text-blue-600 font-bold text-xs uppercase tracking-widest">{t("common.loading")}</div>
              ) : users.length === 0 ? (
                <div className="text-center py-10 text-gray-400 italic">{t("settings.noUsersFound")}</div>
              ) : (
                users.filter(user => isAdmin || user._id === auth?._id).map((user) => (
                  <div
  key={user._id}
  className={`
    p-4
    rounded-xl
    border
    flex
    justify-between
    items-start
    transition-all
    duration-500
    ${
      highlightUserId === user._id
        ? "bg-blue-50 border-blue-300 shadow-[0_0_30px_rgba(59,130,246,0.25)] animate-pulse"
        : "bg-white border-gray-100 shadow-sm"
    }
  `}
>
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5 ${avatarColor(user.name)}`}>{getInitials(user.name)}</div>
                      <div className="space-y-1">
                        <p className="font-bold text-gray-800">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <div className="flex items-center space-x-2 mt-2">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${user.role?.toLowerCase() === 'admin' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>{user.role}</span>
                          <span className="flex items-center space-x-0.5 text-green-600 text-[9px] font-bold"><MdCheckCircle className="w-3 h-3" /><span>{t("settings.active")}</span></span>
                        </div>
                      </div>
                    </div>
                    {isAdmin && (
                      <button 
                        onClick={() => openEditModal(user)}
                        className="p-2 bg-gray-50 text-gray-500 rounded-lg active:bg-blue-100 active:text-blue-600"
                      >
                        <MdEdit className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ) : activeTab === "logs" ? (
          <div>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse text-[12px]">
                <thead>
                  <tr className="bg-gray-50 text-gray-400 uppercase text-[10px] font-bold tracking-widest border-b border-gray-100">
                    <th className="px-6 py-4">{t("settings.time")}</th>
                    <th className="px-6 py-4">{t("settings.actions")}</th>
                    <th className="px-6 py-4">{t("settings.module")}</th>
                    <th className="px-6 py-4">{t("settings.user")}</th>
                    <th className="px-6 py-4">{t("settings.description")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {logs.map((log) => (
                    <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-gray-400 font-medium">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${log.action === 'CREATE' ? 'bg-green-100 text-green-600' : log.action === 'UPDATE' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-bold text-gray-600">{log.module}</td>
                      <td className="px-6 py-4 text-gray-700 font-semibold">{log.user}</td>
                      <td className="px-6 py-4 text-gray-500 italic">{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden grid grid-cols-1 gap-3 p-4 bg-gray-50/50">
              {logs.map((log) => (
                <div 
                  key={log._id} 
                  onClick={() => setSelectedLog(log)}
                  className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 space-y-3 active:scale-[0.98] transition-transform cursor-pointer"
                >
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${log.action === 'CREATE' ? 'bg-green-100 text-green-600' : log.action === 'UPDATE' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                      {log.action}
                    </span>
                    <span className="text-[10px] text-gray-400">{new Date(log.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-700">{log.module}</p>
                    <p className="text-xs text-gray-500 truncate">{log.details}</p>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{t("settings.user")}: {log.user}</span>
                    <span className="text-[10px] text-blue-600 font-bold">{t("settings.closeDetails")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
            <div className="max-w-md space-y-8 px-4 sm:px-6">
              {/* System Logo Upload */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider flex items-center space-x-2">
                  <MdBusiness className="w-4 h-4" />
                  <span>Company Logo</span>
                </h3>
                <div className="flex items-center space-x-6">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-2xl bg-gray-100 border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden transition-all group-hover:border-blue-300">
                      {systemLogo ? (
                        <img src={systemLogo} alt="System Logo" className="w-full h-full object-cover" />
                      ) : (
                        <MdBusiness className="w-10 h-10 text-gray-300" />
                      )}
                    </div>
                    {isAdmin && (
                <label className="absolute -bottom-2 -right-2 bg-blue-600 text-white p-2 rounded-lg cursor-pointer shadow-lg hover:bg-blue-700 transition-all active:scale-95">
                  <MdCameraAlt className="w-4 h-4" />
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileChange(e, 'system')} />
                </label>
              )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Agency Name</label>
<input 
                        type="text"
                        className="w-full border border-gray-200 rounded-lg p-2 text-sm font-bold text-gray-800 outline-none focus:border-blue-500"
                        value={systemName}
                        onChange={(e) => handleSystemNameChange(e.target.value)}
                        readOnly={isStaff}
                      />
                    <p className="text-xs text-gray-500">Upload your agency logo and name to display it in the header and sidebar.</p>
                  </div>
                </div>
              </div>

                {/* Theme Controls */}
                {/* <div className="space-y-4 pt-4 border-t border-gray-50">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Theme Settings</h3>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-600">Dark Mode:</span>
                    <button onClick={() => toggleTheme()} className="p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors">
                      {isDarkMode ? <span className="w-4 h-4 bg-gray-800 rounded-full block"></span> : <span className="w-4 h-4 bg-gray-100 rounded-full block"></span>}
                    </button>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-600">Primary Color:</span>
                      <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-10 p-0 border rounded" />
                    </div>
                  )}
                </div> */}

              <div className="space-y-4 pt-4 border-t border-gray-50">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">{t("settings.language")}</h3>
                <p className="text-xs text-gray-500">{t("settings.selectLanguage")}</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    onClick={() => setLanguage("en")}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${language === 'en' ? 'border-blue-600 bg-blue-50/50' : 'border-gray-100 hover:border-gray-200 bg-white'}`}
                  >
                    <span className={`font-bold ${language === 'en' ? 'text-blue-600' : 'text-gray-600'}`}>{t("settings.english")}</span>
                    {language === 'en' && <MdCheckCircle className="w-5 h-5 text-blue-600" />}
                  </button>
                  
                  <button 
                    onClick={() => setLanguage("ta")}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${language === 'ta' ? 'border-blue-600 bg-blue-50/50' : 'border-gray-100 hover:border-gray-200 bg-white'}`}
                  >
                    <span className={`font-bold ${language === 'ta' ? 'text-blue-600' : 'text-gray-600'}`}>{t("settings.tamil")}</span>
                    {language === 'ta' && <MdCheckCircle className="w-5 h-5 text-blue-600" />}
                  </button>
                </div>
              </div>
            </div>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-800">
                {editingUser ? t("settings.editUserDetails") : t("settings.createNewAccount")}
              </h3>
            </div>
            <form onSubmit={handleUserSubmit} className="p-6 space-y-4">
              <div className="flex justify-center pb-2">
                <div className={`w-20 h-20 rounded-full border-4 border-gray-50 flex items-center justify-center bg-gray-100 shadow-sm text-white text-xl font-bold ${avatarColor(formData.name)}`}>
                  {getInitials(formData.name)}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">{t("settings.fullName")}</label>
                <input 
                  type="text" required
                  className="w-full border border-gray-200 rounded-lg p-2.5 outline-none focus:border-blue-500 transition-colors"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">{t("settings.emailAddress")}</label>
                <input 
                  type="email" required
                  className="w-full border border-gray-200 rounded-lg p-2.5 outline-none focus:border-blue-500 transition-colors"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">{t("settings.password")} {editingUser && t("settings.leaveBlank")}</label>
                <input 
                  type="password" 
                  className="w-full border border-gray-200 rounded-lg p-2.5 outline-none focus:border-blue-500 transition-colors"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editingUser}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">{t("settings.userRole")}</label>
                <select 
                  className="w-full border border-gray-200 rounded-lg p-2.5 outline-none focus:border-blue-500 transition-colors bg-white"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  <option value="Staff">{t("settings.staff")}</option>
                  <option value="Admin">{t("settings.admin")}</option>
                </select>
              </div>

              <div className="pt-4 flex space-x-3">
                <button 
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 font-bold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-md active:scale-95"
                >
                  {editingUser ? t("settings.saveChanges") : t("settings.createAccount")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Audit Log Detail Popover (Mobile Only) */}
      {selectedLog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${selectedLog.action === 'CREATE' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                    {selectedLog.action}
                  </span>
                  <h3 className="text-lg font-bold text-gray-800 mt-2">{selectedLog.module}</h3>
                </div>
                <button 
                  onClick={() => setSelectedLog(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{t("settings.time")}</p>
                  <p className="text-gray-700 font-medium">{new Date(selectedLog.timestamp).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{t("settings.performedBy")}</p>
                  <p className="text-gray-700 font-medium">{selectedLog.user}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{t("settings.description")}</p>
                  <p className="text-gray-700 leading-relaxed">{selectedLog.details}</p>
                </div>
                {selectedLog.data && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{t("settings.rawData")}</p>
                    <pre className="text-[10px] text-gray-500 overflow-x-auto whitespace-pre-wrap leading-tight">
                      {JSON.stringify(selectedLog.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gray-100 p-4">
              <button 
                onClick={() => setSelectedLog(null)}
                className="w-full py-2.5 bg-white text-gray-700 font-bold rounded-xl shadow-sm border border-gray-200"
              >
                {t("settings.closeDetails")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
